import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { teamService } from '@/services/amai/team.service';
import { spaceManager } from '@/services/space/spaceManager';
import { AccountListItem, AccountType } from '@/services/types';
import { selectCurrentSpace } from '@/store/selectors/spaceSelectors';
import { getUserInfo, maskMobile } from '@/utils/auth';
import { eventBus } from '@/utils/eventBus';

/**
 * 账户菜单数据源（V0.1 团队协作）
 *
 * 职责：
 * 1. 拉取账户列表（个人 + 团队），团队成员在前、拥有者在后（PRD 1.1）
 * 2. 提供当前身份三态 + 菜单按钮显隐（PRD 状态表）
 * 3. 浮层每次打开强制重拉（米粒余额/团队列表保持新鲜）
 */
/** 是否使用模拟账户数据（真实接口 POST /v1/user/account_list 已就绪，默认关闭；仅后端异常需 UI 演示时临时置 true） */
const USE_MOCK_ACCOUNT_LIST = false;

/** 模拟账户列表：个人账户（取真实登录用户信息，保证至少一项）+ 三个示例团队（成员在前、拥有者在后） */
const buildMockAccountList = (): AccountListItem[] => {
  const info = getUserInfo();
  const personal: AccountListItem = {
    user_avatar: info?.user_avatar || '',
    user_id: String(info?.user_id ?? ''),
    user_name: info?.user_name || '',
    mobile: info?.mobile || '',
    auth_identifier: info?.auth_identifier || '',
    personal_profile: '',
    wechat: '',
    auth_level: '',
    apply_auth: 0,
    is_member: true,
    total_credits: 5790500,
    account_type: AccountType.Personal,
  };
  const mockTeam = (id: string, name: string, team_role: 1 | 2): AccountListItem => ({
    user_avatar: '',
    user_id: id,
    user_name: name,
    mobile: '',
    auth_identifier: '',
    personal_profile: '',
    wechat: '',
    auth_level: '',
    apply_auth: 0,
    is_member: true,
    total_credits: 0,
    account_type: AccountType.Team,
    team_role,
  });
  return [
    personal,
    mockTeam('mock_team_1', '星河影视', 2),
    mockTeam('mock_team_2', '界墨演示团队', 1),
    mockTeam('mock_team_3', '1', 1),
  ];
};

export const useAccountMenuData = () => {
  const navigate = useNavigate();
  const currentSpace = useSelector(selectCurrentSpace);
  const [accountList, setAccountList] = useState<AccountListItem[]>([]);
  const [loading, setLoading] = useState(false);
  /** loading 需求来自「切换账户」展开时的骨架态，用 ref 防重复触发 */
  const fetchingRef = useRef(false);

  /** 拉账户列表（弹层打开时调用） */
  const fetchAccountList = useCallback(async (silent = false) => {
    if (USE_MOCK_ACCOUNT_LIST) {
      // 模拟数据同步返回，无 loading 态
      const list = buildMockAccountList();
      setAccountList(list);
      if (list[0]?.user_id) {
        spaceManager.recordPersonalUserId(list[0].user_id);
      }
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!silent) setLoading(true);
    try {
      const response = await teamService.getAccountList();
      if (response.code === 0 && response.data) {
        const { personal_account, team_account_list = [] } = response.data;
        // 团队列表排序：成员在前、拥有者在后；个人账户置顶由展示层处理
        const sorted = [...team_account_list].sort(
          (a, b) => (a.team_role ?? 2) - (b.team_role ?? 2)
        );
        setAccountList([personal_account, ...sorted]);
        // 顺手记录个人 user_id（画布 creator_user_id 比对用）
        if (personal_account?.user_id) {
          spaceManager.recordPersonalUserId(personal_account.user_id);
        }
      }
    } catch (error) {
      console.error('[useAccountMenuData] 获取账户列表失败:', error);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  /** 切换空间 */
  const switchToAccount = useCallback(
    async (account: AccountListItem): Promise<boolean> => {
      const isPersonal = account.account_type === AccountType.Personal || !account.team_role;
      const target = isPersonal
        ? null
        : {
            teamId: account.user_id,
            teamName: account.user_name,
            role: account.team_role === 1 ? (1 as const) : (2 as const),
          };
      const ok = await spaceManager.switchToSpace(target, navigate);
      return ok;
    },
    [navigate]
  );

  // 监听空间变化：切换成功后重拉（新 token 下列表/余额已变）
  useEffect(() => {
    const handler = () => fetchAccountList(true);
    eventBus.on('spaceChanged', handler);
    return () => {
      eventBus.off('spaceChanged', handler);
    };
  }, [fetchAccountList]);

  const userInfo = getUserInfo();

  return {
    currentSpace,
    userInfo,
    maskedMobile: userInfo?.mobile ? maskMobile(userInfo.mobile) : userInfo?.auth_identifier || '',
    accountList,
    loading,
    fetchAccountList,
    switchToAccount,
    isSwitching: spaceManager.isSwitching,
  };
};
